import { create } from "zustand";
import { Result as DeviceResult } from "@/data/device-messages";
import { createEmptyWebPageContent } from "../res/placeholderData";
import {
  EditableDataExportObject,
  EditableDataObject,
  toEditable,
} from "@/lib/editableData";
import { Evaluation } from "@/components/FeedbackMessage";
import {
  CampaignPageOutput,
  LandingPageOutput,
  PadPageOutput,
} from "@/data/web-pages/types.SCHEMA";
import { WebPage } from "@/types/webPage";

export type WebPageType = keyof WebPageContentState;

const invalidatedDevices: Partial<WebPageState> = {
  webPageContent: createEmptyWebPageContent(),
};

const invalidatedBrief: Partial<WebPageState> = {
  brief: new EditableDataObject(""),
  ...invalidatedDevices,
};

export interface WebPageState {
  productDescription: string;
  tonality: number;
  useBulletPoints: boolean;
  additionalInfo: string;
  brief: EditableDataObject<string>;
  sources: string[];
  selectedWebPages: WebPage[];
  feedback: Record<string, Evaluation>;
  webPageContent: {
    landing: EditableDataObject<LandingPageOutput>;
    pad: EditableDataObject<PadPageOutput>;
    campaign: EditableDataObject<CampaignPageOutput>;
  };
}

export interface WebPageStateExport {
  productDescription: string;
  tonality: number;
  useBulletPoints: boolean;
  additionalInfo: string;
  brief: EditableDataExportObject<string>;
  sources: string[];
  selectedWebPages: WebPage[];
  feedback: Record<string, Evaluation>;
  webPageContent: {
    landing: EditableDataExportObject<LandingPageOutput>;
    pad: EditableDataExportObject<PadPageOutput>;
    campaign: EditableDataExportObject<CampaignPageOutput>;
  };
}

export type WebPageContentState = WebPageState["webPageContent"];

export interface WebPageActions {
  setProductDescription: (m: string) => void;
  setTonality: (t: number) => void;
  setUseBulletPoints: (v: boolean) => void;
  setAdditionalInfo: (v: string) => void;

  setSelectedWebPages: (c: WebPage[]) => void;
  addToSelectedWebPages: (c: WebPage) => void;

  setBrief: (m: EditableDataObject<string>) => void;
  setSources: (m: string[]) => void;
  setFeedback: (name: string, feedback: Evaluation) => void;
  deleteFeedback: () => void;

  setWebPageContent: (m: WebPageContentState) => void;
  updateWebPageContent: (m: Partial<WebPageContentState>) => void;

  setContentForPageType: (
    data: EditableDataObject<DeviceResult>,
    pageType: WebPageType,
  ) => void;
  importState: (state: WebPageStateExport) => void;
}

export type WebPageStore = WebPageState & WebPageActions;

const initialWebPageData = createEmptyWebPageContent();

const useWebPageStore = create<WebPageStore>((set, get) => ({
  productDescription: "",
  setProductDescription: m =>
    set({
      productDescription: m,
      ...invalidatedBrief,
    }),
  tonality: 5,
  setTonality: n =>
    set({
      tonality: n,
      ...invalidatedDevices,
    }),
  useBulletPoints: false,

  selectedWebPages: ["landing"] as WebPage[],
  setSelectedWebPages: c => set({ selectedWebPages: c }),
  addToSelectedWebPages: c =>
    set({ selectedWebPages: [...get().selectedWebPages, c] }),
  setUseBulletPoints: (v: boolean) =>
    set({
      useBulletPoints: v,
      ...invalidatedBrief,
    }),
  additionalInfo: "",
  setAdditionalInfo: (v: string) =>
    set({ additionalInfo: v, ...invalidatedBrief }),

  brief: new EditableDataObject(""),
  setBrief: m => set({ brief: m, webPageContent: initialWebPageData }),

  sources: [],
  setSources: m => set({ sources: m }),

  feedback: {},
  setFeedback: (name, feedback) => {
    set({ feedback: { ...get().feedback, [name]: feedback } });
  },
  deleteFeedback: () => set({ feedback: {} }),

  webPageContent: initialWebPageData,
  setWebPageContent: d => set({ webPageContent: d }),
  updateWebPageContent: d => {
    return set({ webPageContent: { ...get().webPageContent, ...d } });
  },

  setContentForPageType: (data, device) => {
    const newWebPageContent = { ...get().webPageContent, [device]: data };
    return set({ webPageContent: newWebPageContent });
  },

  importState: state => {
    const emptyWebPageContent = createEmptyWebPageContent();
    const newState: WebPageState = {
      ...state,
      selectedWebPages: state.selectedWebPages ?? ["landing"],

      brief: new EditableDataObject("").import(state.brief ?? toEditable("")),
      webPageContent: {
        landing: new EditableDataObject<LandingPageOutput>({
          ...emptyWebPageContent.landing.get(),
        }).import(
          state.webPageContent?.landing ??
            toEditable({ ...emptyWebPageContent.landing.get() }),
        ),
        campaign: new EditableDataObject<CampaignPageOutput>({
          ...emptyWebPageContent.campaign.get(),
        }).import(
          state.webPageContent?.campaign ??
            toEditable({ ...emptyWebPageContent.campaign.get() }),
        ),
        pad: new EditableDataObject<PadPageOutput>({
          ...emptyWebPageContent.campaign.get(),
        }).import(
          state.webPageContent?.pad ??
            toEditable({ ...emptyWebPageContent.campaign.get() }),
        ),
      },
    };
    return set({ ...newState });
  },
}));

export const useWebStateFields: () => WebPageState = () =>
  useWebPageStore(state => ({
    productDescription: state.productDescription,
    tonality: state.tonality,
    useBulletPoints: state.useBulletPoints,
    additionalInfo: state.additionalInfo,
    brief: state.brief,
    sources: state.sources,
    feedback: state.feedback,
    webPageContent: state.webPageContent,
    selectedWebPages: state.selectedWebPages,
  }));

export default useWebPageStore;
